import { ApiError } from '@/api/auth';
import type { MfaMethod } from '@/api/mfa';

// High-value step-up (SEC-07, issue #159). At or above HIGH_VALUE_THRESHOLD_CENTS
// the server refuses a spend by an MFA-enrolled fan with 403 step_up_required
// until the request carries a single-use token from POST /me/step-up. This is
// the platform-free half of the client flow — spot the refusal, get a token,
// send the SAME action again with it — kept apart from the prompt so it can be
// tested without rendering anything.
//
// The refused request moved no money: every guarded endpoint runs the check
// before it charges (see the requireStepUp call sites in internal/ticket and
// internal/wallet). That is what makes cancelling the prompt free.

/** What the prompt needs to know about a refusal. */
export interface StepUpChallenge {
  method: MfaMethod;
}

/** The fan closed the prompt. Nothing was charged; screens show nothing for it. */
export class StepUpCancelledError extends Error {
  /** A brand, so a plain Error is not structurally a cancellation — without it
   *  isStepUpCancelled would narrow a screen's `Error` to never on the else side. */
  readonly stepUpCancelled = true;

  constructor() {
    super('Step-up cancelled');
    this.name = 'StepUpCancelledError';
  }
}

export const isStepUpCancelled = (e: unknown): e is StepUpCancelledError => e instanceof StepUpCancelledError;

/** The challenge a refusal asks for, or null when `e` is not a step-up refusal.
 *  The server names the enrolled method in details; an app code is the default
 *  because it needs nothing sent. */
export function stepUpChallengeFrom(e: unknown): StepUpChallenge | null {
  if (!(e instanceof ApiError) || e.code !== 'step_up_required') return null;
  return { method: e.details?.method === 'sms' ? 'sms' : 'totp' };
}

/** Resolves to a step-up token, or rejects with StepUpCancelledError. */
export type StepUpPrompt = (challenge: StepUpChallenge) => Promise<string>;

/**
 * Run a guarded money call, challenging once if the server asks.
 *
 * `action` is called with no token first — below the threshold, or for a fan
 * with no second factor, that is the whole story. On step_up_required it is
 * called exactly once more with the token. Once, not in a loop: a token is
 * minted for this call and spent by it, so a second refusal is a real error
 * (say the fan left the prompt open past the token's life) and is surfaced
 * rather than answered with another prompt.
 *
 * `action` must build a fresh request each call. The retry has a different body
 * (it carries the token), so it needs its own Idempotency-Key — reusing the
 * refused request's key would be a different payload under the same key.
 */
export async function runWithStepUp<T>(
  action: (stepUpToken?: string) => Promise<T>,
  prompt: StepUpPrompt,
): Promise<T> {
  try {
    return await action();
  } catch (e) {
    const challenge = stepUpChallengeFrom(e);
    if (!challenge) throw e;
    return action(await prompt(challenge));
  }
}
